import { formaterMontant } from './format';
import type { CategorieDestinataire, FicheDestinataire } from '../types';

/**
 * RELANCE D'UN IMPAYE (2026-10-17) — le courriel que « Relancer » ouvre
 * dans Gmail depuis le rapprochement, prerempli. Tout est calcule ici,
 * en pur : le texte suit le modele valide par Georges le 2026-10-17, en
 * deux versions — a l'adulte directement, au parent pour un mineur.
 *
 * C'est le premier temps : un brouillon Gmail, pas un envoi par l'API ni
 * une trace en base (lot 3). Le texte est donc celui que Gmail recevra
 * dans l'URL, en clair, sans mise en forme.
 */

export interface DonneesRelance {
  /** Nom court de l'ecole, pour l'objet (« Artistic »). */
  ecole: string;
  /** Raison sociale, pour l'ordre du cheque (« Association Artistic »). */
  raisonSociale: string;
  prenom: string;
  nom: string;
  /** En centimes, positif : ce qui reste a payer. */
  resteDu: number;
  /** L'adhesion de l'annee existe et n'est pas soldee : le message la nomme. */
  cotisationNonSoldee: boolean;
  /** true = mineur, false = adulte, null = age inconnu. */
  mineur: boolean | null;
  compte: { titulaire: string; iban: string; bic: string | null } | null;
}

const PARENTS: CategorieDestinataire[] = ['mere', 'pere', 'tuteur'];

/** Par groupes de quatre, pour l'oeil. */
const formaterIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

export function sujetRelance(d: DonneesRelance): string {
  return `${d.ecole} | Règlement de l’inscription`;
}

export function corpsRelance(d: DonneesRelance): string {
  const auParent = d.mineur !== false;
  const montant = formaterMontant(d.resteDu);
  // La reference du virement : NOM Prenom de l'adherent, rien d'autre
  // (Georges, 2026-10-17) — c'est ce que la banque affiche et ce que le
  // rapprochement cherche.
  const libelle = `${d.nom.toUpperCase()} ${d.prenom}`;
  // « à l'ordre de l’Association Artistic », « à l'ordre de Domusic » :
  // l'elision suit l'initiale de la raison sociale.
  const ordre = /^[aeiouyhàâéèêîïôû]/i.test(d.raisonSociale) ? `l’${d.raisonSociale}` : d.raisonSociale;

  const entete = auParent
    ? `Bonjour,\n\nÀ ce jour, il reste ${montant} à régler pour couvrir l’inscription${
        d.cotisationNonSoldee ? ' et la cotisation annuelle à l’association' : ''
      } de votre enfant ${d.prenom}.`
    : `Bonjour ${d.prenom},\n\nÀ ce jour, il reste ${montant} à régler pour couvrir votre inscription${
        d.cotisationNonSoldee ? ' et votre cotisation annuelle à l’association' : ''
      }.`;

  const moyens =
    `Pour solder ce montant, vous pouvez :\n` +
    `- déposer un ou plusieurs chèques à l’ordre de ${ordre} au secrétariat, qui les encaissera aux dates que vous indiquerez au dos ;\n` +
    `- faire un virement sur le compte de l’association, en précisant en libellé « ${libelle} » ;\n` +
    `- régler en espèces au secrétariat, aux horaires d’ouverture.`;

  // Sans IBAN saisi sur la fiche de l'ecole, le bloc disparait : on
  // n'envoie pas des crochets a une famille (decision du 2026-10-17).
  const banque = d.compte
    ? `\n\nNos coordonnées bancaires sont les suivantes :\n- IBAN : ${formaterIban(d.compte.iban)}` +
      (d.compte.bic ? `\n- BIC : ${d.compte.bic}` : '')
    : '';

  return `${entete}\n\n${moyens}${banque}\n\nMerci pour votre confiance.\nBien cordialement,\n`;
}

/**
 * A qui ecrire : les parents d'un mineur, l'adulte lui-meme ; a defaut,
 * les autres adresses de la fiche. Age inconnu : les parents s'il y en a,
 * sinon l'adherent. Dedoublonne sur l'email en minuscules.
 */
export function destinatairesRelance(fiche: FicheDestinataire, mineur: boolean | null): string[] {
  const par = (categories: CategorieDestinataire[]) =>
    fiche.adresses.filter((a) => categories.includes(a.categorie)).map((a) => a.email.trim());
  const parents = par(PARENTS);
  const adherent = par(['adherent']);
  const autres = par(['conjoint', 'autre']);
  let choix: string[];
  if (mineur === false) choix = adherent.length > 0 ? adherent : [...parents, ...autres];
  else choix = parents.length > 0 ? parents : [...adherent, ...autres];
  const vus = new Set<string>();
  return choix.filter((e) => {
    const cle = e.toLowerCase();
    if (vus.has(cle)) return false;
    vus.add(cle);
    return true;
  });
}

/** Le brouillon Gmail : fenetre de composition, destinataires directs, objet et corps. */
export function lienGmailRelance(expediteur: string, destinataires: string[], sujet: string, corps: string): string {
  return (
    'https://mail.google.com/mail/?' +
    new URLSearchParams({ authuser: expediteur, view: 'cm', fs: '1', to: destinataires.join(','), su: sujet, body: corps }).toString()
  );
}
